import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Play, Check, ArrowLeft } from "lucide-react";
import { Poster, ProgressLine } from "@/components/Poster";
import { api, epLabel, fmtRuntime, pct } from "@/lib/api";

const Episode = ({ e }) => (
  <Link to={`/watch/${e.id}`} className="group flex gap-5 rounded-lg border border-transparent p-3 transition-colors hover:border-zinc-200 hover:bg-white" data-testid={`episode-row-${e.id}`}>
    <div className="relative aspect-video w-44 shrink-0 overflow-hidden rounded-md bg-zinc-100">
      <Poster id={e.id} type="still" alt={e.title} className="h-full w-full" iconSize={22} />
      {!e.progress?.watched && <ProgressLine value={pct(e.progress)} className="absolute bottom-0 left-0" />}
    </div>
    <div className="min-w-0 flex-1 py-1">
      <p className="font-mono text-[11px] text-amber-700">{epLabel(e)}{e.duration ? ` · ${fmtRuntime(e.duration)}` : ""}</p>
      <p className="mt-1 flex items-center gap-2 truncate text-sm font-medium">{e.title}{e.progress?.watched && <Check size={13} className="text-amber-600" />}</p>
      {e.summary && <p className="mt-1 line-clamp-2 text-xs text-zinc-500">{e.summary}</p>}
    </div>
  </Link>
);

export default function ShowDetail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [show, setShow] = useState(null);
  const [season, setSeason] = useState(null);
  useEffect(() => {
    api.get("/shows/detail", { params: { key: params.get("key") } }).then((r) => {
      setShow(r.data);
      setSeason(r.data.next_up?.season ?? r.data.episodes[0]?.season ?? null);
    }).catch(() => navigate("/shows"));
  }, [params, navigate]);
  const seasons = useMemo(() => [...new Set((show?.episodes || []).map((e) => e.season))], [show]);
  if (!show) return <div className="h-64 animate-pulse rounded-xl bg-zinc-100" />;
  const eps = show.episodes.filter((e) => e.season === season);
  return (
    <div>
      <Link to="/shows" className="mb-8 inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900" data-testid="show-back"><ArrowLeft size={12} />All shows</Link>
      <div className="mb-12 flex flex-col gap-10 sm:flex-row animate-rise">
        <div className="aspect-[2/3] w-48 shrink-0 overflow-hidden rounded-md border border-zinc-200 bg-white"><Poster id={show.poster_id} alt={show.name} className="h-full w-full" /></div>
        <div className="flex flex-col justify-end">
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-700">TV Show · {show.episodes.length} episodes</p>
          <h1 className="mt-3 font-heading text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl" data-testid="show-title">{show.name}</h1>
          {show.summary && <p className="mt-4 max-w-2xl text-sm leading-relaxed text-zinc-600">{show.summary}</p>}
          {show.next_up && (
            <Link to={`/watch/${show.next_up.id}`} data-testid="show-next-up"
              className="mt-6 inline-flex h-11 w-fit items-center gap-2 rounded-full bg-zinc-900 px-5 text-sm text-white transition-[background-color,transform] hover:bg-zinc-800 active:scale-[0.98]">
              <Play size={14} fill="currentColor" />{show.next_up.progress ? "Resume" : "Play"} {epLabel(show.next_up)}
            </Link>
          )}
        </div>
      </div>
      <div className="mb-6 flex flex-wrap gap-1" data-testid="season-tabs">
        {seasons.map((s) => (
          <button key={s ?? "x"} onClick={() => setSeason(s)} data-testid={`season-tab-${s}`}
            className={`h-9 rounded-full px-4 text-sm transition-colors ${season === s ? "bg-zinc-900 text-white" : "border border-zinc-200 bg-white text-zinc-600 hover:text-zinc-900"}`}>
            {s ? `Season ${s}` : "Specials"}
          </button>
        ))}
      </div>
      <div className="space-y-1" data-testid="episode-list">{eps.map((e) => <Episode key={e.id} e={e} />)}</div>
    </div>
  );
}
